import { i18n } from 'next-i18next'
import { Schema, date, object, string } from 'yup'
import { Config } from 'modules/config/state/config-types'
import { OrderContactAnswersSlice } from './contact-answers-types'
import { PHONE_VALIDATION_REGEX, FIELD_CHARACTER_LIMITS } from 'utils/constants/misc'
import { Question } from 'models/Question'
// @ts-expect-error i18n-zipcodes is a pure JS library and does not have types
import i18nZipcode from 'i18n-zipcodes'
import { getLocalizedLabels, countryHasStates } from '../address/address-utils'
import { ADDRESS_TAGS } from 'modules/config/utils/address-questions-utils'

type Tag = NonNullable<Question['tag']>

// Mapping from backend question tags to internal field names for address fields
const addressTagMapping: Partial<Record<Tag, string>> = {
  address: 'addressLine1',
  address2: 'addressLine2',
  postal_code: 'zipCode',
  country: 'country',
  city: 'city',
  state: 'state',
}

export const makeOrderContactAnswersValidationSchema = (config: Config) => {
  const t = i18n?.t.bind(i18n) || ((_key: string, fallback: string) => fallback)

  const MAX_LENGTH_MESSAGE = t('ContactAnswers.validation.limitExceeded', 'You are over the limit')

  const schemaFields = config.questions.contact.reduce((acc, cur) => {
    // TODO: Re-enable address questions when custom address questions are available again
    // Skip address questions and questions without tags
    if (!cur.tag || ADDRESS_TAGS.includes(cur.tag as (typeof ADDRESS_TAGS)[number])) {
      return acc
    }

    // Use the backend tag name for schema keys (map address fields only)
    const fieldName = addressTagMapping[cur.tag] || cur.tag

    // Get appropriate character limit based on field type
    const getCharacterLimit = (tag: string) => {
      switch (tag) {
        case 'address':
          return FIELD_CHARACTER_LIMITS.ADDRESS_LINE_1
        case 'address2':
          return FIELD_CHARACTER_LIMITS.ADDRESS_LINE_2
        case 'city':
          return FIELD_CHARACTER_LIMITS.CITY
        case 'state':
          return FIELD_CHARACTER_LIMITS.STATE
        case 'middle_name':
          return FIELD_CHARACTER_LIMITS.MIDDLE_NAME
        case 'company_name':
          return FIELD_CHARACTER_LIMITS.COMPANY_NAME
        case 'cellphone':
        case 'homephone':
          return FIELD_CHARACTER_LIMITS.PHONE
        default:
          return 255 // Default for other fields
      }
    }

    const lenTestFn = (value?: string) =>
      value === undefined ? true : value.length <= getCharacterLimit(cur.tag || '')

    const getTruncationMessage = (tag: string) => {
      const limit = getCharacterLimit(tag)

      switch (tag) {
        case 'address':
          return t('validation.truncation.addressLine1', 'Address must be 512 characters or less')
        case 'address2':
          return t(
            'validation.truncation.addressLine2',
            'Address line 2 must be 45 characters or less',
          )
        case 'city':
          return t('validation.truncation.city', 'City must be 50 characters or less')
        case 'state':
          return t('validation.truncation.state', 'State must be 100 characters or less')
        case 'middle_name':
          return t('validation.truncation.middleName', 'Middle name must be 32 characters or less')
        case 'company_name':
          return t(
            'validation.truncation.companyName',
            'Company name must be 32 characters or less',
          )
        case 'cellphone':
        case 'homephone':
          return t('validation.truncation.phone', 'Phone number must be 16 characters or less')
        default:
          return t(
            'validation.truncation.customField',
            `This field must be ${limit} characters or less`,
          )
      }
    }

    const requiredTestFn = (value?: string | boolean | number | Date) =>
      cur.is_required ? !!value : true

    // Get field-specific error messages
    const getRequiredMessage = (tag: string) => {
      switch (tag) {
        case 'prefix':
          return t('ContactAnswers.validation.prefixRequired', 'Enter one of the provided options')
        case 'middle_name':
          return t('ContactAnswers.validation.middleNameRequired', 'Enter your middle name')
        case 'suffix':
          return t('ContactAnswers.validation.suffixRequired', 'Enter one of the provided options')
        case 'gender':
          return t('ContactAnswers.validation.genderRequired', 'Enter one of the provided options')
        case 'pronoun':
          return t('ContactAnswers.validation.pronounRequired', 'Enter one of the provided options')
        case 'cellphone':
        case 'homephone':
          return t('ContactAnswers.validation.invalidPhone', 'Enter a valid phone number')
        case 'birthdate':
          return t('ContactAnswers.validation.birthdayRequired', 'Enter your birthday')
        case 'company_name':
          return t('ContactAnswers.validation.companyNameRequired', 'Enter your company name')
        case 'website':
          return t('ContactAnswers.validation.websiteRequired', 'Enter your website')
        // Address field error messages
        case 'address':
          return t('AddressFields.validations.address', 'Enter your address')
        case 'city':
          return t('AddressFields.validations.city', 'Enter a city')
        case 'state':
          return t('AddressFields.validations.state', 'Enter a state')
        case 'postal_code':
          return t('AddressFields.validations.zipCode', 'Enter a valid zip code')
        case 'country':
          return t('AddressFields.validations.country', 'Enter your country')
        default:
          return t('ContactAnswers.validation.fieldRequired', 'This field is required')
      }
    }

    if (cur.tag === 'cellphone' || cur.tag === 'homephone') {
      acc[fieldName] = string()
        .test('required', getRequiredMessage(cur.tag), requiredTestFn)
        .test('len', getTruncationMessage(cur.tag), lenTestFn)
        .matches(PHONE_VALIDATION_REGEX, {
          message: t('ContactAnswers.validation.invalidPhone', 'Enter a valid phone number'),
          excludeEmptyString: true,
        })
    } else if (cur.tag === 'birthdate') {
      const today = new Date()

      const aHunderedAndFiftyYearsAgo = new Date()
      aHunderedAndFiftyYearsAgo.setFullYear(today.getFullYear() - 150)

      acc[fieldName] = date()
        .test('required', getRequiredMessage(cur.tag), requiredTestFn)
        .min(
          aHunderedAndFiftyYearsAgo,
          t('ContactAnswers.validation.invalidBirthday', 'Enter a valid birthday'),
        )
        .max(today, t('ContactAnswers.validation.invalidBirthday', 'Enter a valid birthday'))
        .transform((curr, orig) => (orig === '' || orig === null ? undefined : curr))
    } else if (cur.tag === 'postal_code') {
      // Special validation for postal code with country-specific rules (mapped to zipCode)
      acc[fieldName] = string()
        .test('required', (value, context) => {
          if (!cur.is_required) return true
          if (!!value) return true

          // Get country from the form context for country-specific error message
          const country = context.parent?.country || 'US'
          const isUS = country === 'US'

          const validationKey = isUS
            ? 'AddressFields.validations.zipCode'
            : 'AddressFields.validations.postalCode'

          const fallbackMessage = isUS ? 'Enter a valid zip code' : 'Enter a valid postal code'

          return context.createError({
            message: t(validationKey, fallbackMessage),
          })
        })
        .test('postal_code', (value, context) => {
          if (!value) return !cur.is_required // If not required and empty, it's valid

          // Get country from the form context
          const country = context.parent?.country || 'US'
          const requiredLabel = getLocalizedLabels(country).postalCode

          try {
            const isValid = i18nZipcode(country, value)

            return (
              isValid ||
              t('ContactAnswers.validation.invalidPostalCode', `${requiredLabel} is invalid`)
            )
          } catch (e) {
            // If country is not supported by i18n-zipcodes, assume it's valid
            return true
          }
        })
    } else if (cur.tag === 'address2') {
      // Address 2 is always optional per requirements (mapped to addressLine2)
      acc[fieldName] = string().test('len', MAX_LENGTH_MESSAGE, lenTestFn)
    } else if (cur.tag === 'state') {
      acc[fieldName] = string()
        .test('required', getRequiredMessage(cur.tag), (value, context) => {
          if (!cur.is_required) return true // If not marked as required, it's optional

          // Get country from the form context
          const country = context.parent?.country
          if (!country) return true

          return countryHasStates(country) ? !!value : true
        })
        .test('len', getTruncationMessage(cur.tag), lenTestFn)
    } else {
      switch (cur.type) {
        case 'string':
        case 'text':
          acc[fieldName] = string()
            .test('required', getRequiredMessage(cur.tag), requiredTestFn)
            .test('len', getTruncationMessage(cur.tag), lenTestFn)

          break

        case 'enum':
          acc[fieldName] = string().test('required', getRequiredMessage(cur.tag), requiredTestFn)

          break
        default:
          console.warn(`Unsupported type: ${cur.type}`)

          break
      }
    }

    return acc
  }, {} as Record<string, Schema>)

  return object(schemaFields)
}

/* eslint-disable max-len */
export const makeOrderContactAnswersPayload = (config: Config, value: OrderContactAnswersSlice) => {
  return config.questions.contact
    .filter((question) => {
      // TODO: Re-enable address questions when custom address questions are available again
      // Filter out address questions
      return question.tag && !ADDRESS_TAGS.includes(question.tag as (typeof ADDRESS_TAGS)[number])
    })
    .map((question) => {
      const tag = question.tag
      //@ts-expect-error | question_id was typed as non-null, but can actually be undefined for ContactQuestions
      // So we assert the type here even though it's technically false since fully fixing the error might break draft transactions
      // TODO: either update draft transactions (and any other uses of question.id) to allow undefined here, or
      // update this code (or makeConfig) to set a default question id based on tag.
      const id = 'id' in question ? question.id : (undefined as number)

      // If no tag defined, return empty answer with question ID (if available)
      if (!tag) {
        return { question_id: id, answer: '' }
      }

      // Map tag to the correct key in OrderContactAnswersSlice (address fields only)
      const actualKey = (addressTagMapping[tag] || tag) as keyof OrderContactAnswersSlice

      const answer = value[actualKey] as string | undefined

      return {
        question_id: id,
        answer: answer || '',
      }
    })
    .filter((answer) => !!answer.answer)
}
